import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Truck, BadgeCheck, Handshake, ArrowUpRight, Boxes, Wine, Cpu, UtensilsCrossed, Sofa } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import ProductCard from '@/components/ProductCard';
import PageHead from '@/components/PageHead';
import { fetchProducts, fetchBrands, CATEGORIES } from '@/lib/api';
import { useQuote } from '@/context/QuoteContext';

const CATEGORY_ICONS = {
  'alimentation': UtensilsCrossed,
  'maison-decoration': Sofa,
  'multimedia-informatique': Cpu,
  'vins-spiritueux': Wine,
};

const CATEGORY_IMAGES = {
  'alimentation': 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=900&q=80',
  'maison-decoration': 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=900&q=80',
  'multimedia-informatique': 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?auto=format&fit=crop&w=900&q=80',
  'vins-spiritueux': 'https://images.unsplash.com/photo-1590746702834-12e802e1eca3?auto=format&fit=crop&w=900&q=80',
};

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const { openQuote } = useQuote();

  useEffect(() => {
    fetchProducts().then((items) => setProducts(items.slice(0, 8))).catch(() => {});
    fetchBrands().then(setBrands).catch(() => {});
  }, []);

  return (
    <div className="flex flex-col" data-testid="home-page">
      <PageHead title="Accueil" description="ALOFI SA — Grossiste & Détaillant B2B sous l'enseigne Intermarché. Approvisionnement de qualité pour supermarchés et commerces. Demandez votre devis." />
      {/* HERO */}
      <section className="relative overflow-hidden hero-bg noise-overlay" data-testid="home-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }} className="lg:col-span-7">
            <Badge variant="outline" className="bg-white/80 border-border tracking-[0.08em] uppercase text-[10px]">
              Grossiste B2B · France
            </Badge>
            <h1 className="mt-4 font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-balance leading-[1.05]">
              Votre Partenaire en
              <span className="text-[hsl(186_55%_38%)]"> Approvisionnement</span>
              <br />— Qualité et Prix Garantis.
            </h1>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed">
              ALOFI SA accompagne supermarchés, commerces de détail et revendeurs avec une
              gamme très large de produits soigneusement sélectionnés, aux meilleurs prix.
              Achetez plus, payez moins.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button onClick={() => openQuote()} size="lg" className="rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="home-hero-primary-cta">
                Demander un devis <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button asChild variant="secondary" size="lg" className="rounded-xl border border-border" data-testid="home-hero-secondary-cta">
                <Link to="/boutique">Voir la boutique</Link>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-[hsl(186_55%_38%)]" /> B2B uniquement</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[hsl(186_55%_38%)]" /> Réponse sous 24–48h</span>
              <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-[hsl(186_55%_38%)]" /> Livraison France</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-[var(--shadow-lift)] bg-white">
              <img src="https://images.pexels.com/photos/5775099/pexels-photo-5775099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Entrepôt logistique premium" className="w-full h-[420px] object-cover" />
            </div>
            <Card className="absolute -bottom-6 -left-6 hidden sm:block w-64 p-4 rounded-2xl bg-white border-border shadow-[var(--shadow-lift)]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center">
                  <Boxes className="w-5 h-5 text-[hsl(186_55%_38%)]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">+ 1000</div>
                  <div className="font-heading font-semibold text-sm leading-snug">références disponibles</div>
                </div>
              </div>
            </Card>
            <Card className="absolute -top-4 -right-2 hidden md:block w-56 p-4 rounded-2xl bg-white border-border shadow-[var(--shadow-soft)] float-slow">
              <div className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Marques partenaires</div>
              <div className="font-heading font-semibold text-2xl mt-1">+13</div>
              <div className="text-xs text-muted-foreground mt-0.5">Apple, Samsung, Smeg…</div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES (Bento) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="home-categories">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px]">Catégories</Badge>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Quatre univers, un seul partenaire</h2>
            <p className="mt-2 text-muted-foreground max-w-xl">De l’alimentaire à l’électronique, en passant par la maison et les vins fins, nous distribuons des produits sélectionnés.</p>
          </div>
          <Button asChild variant="ghost" className="text-foreground hover:text-[hsl(186_55%_38%)]">
            <Link to="/boutique">Toute la boutique <ArrowUpRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {CATEGORIES.map((c, idx) => {
            const Icon = CATEGORY_ICONS[c.slug] || Boxes;
            return (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: idx * 0.05 }}
              >
                <Link
                  to={`/boutique?category=${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className="group block rounded-2xl border border-border bg-white overflow-hidden hover:shadow-[var(--shadow-lift)] transition-shadow"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={CATEGORY_IMAGES[c.slug]} alt={c.name} className="w-full h-full object-cover product-image-zoom" />
                  </div>
                  <div className="p-5 flex items-start justify-between gap-3">
                    <div>
                      <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-[hsl(186_55%_38%)]" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg">{c.name}</h3>
                      <p className="text-xs text-muted-foreground mt-1">Découvrir la sélection</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-muted-foreground group-hover:text-[hsl(186_55%_38%)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* APPROVISIONNEMENT BLOCK */}
      <section className="py-16 bg-secondary/40 border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="order-2 md:order-1">
            <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px]">Approvisionnement</Badge>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Achetez plus. Payez moins.</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Avec une large gamme de références, nous accompagnons les professionnels dans l’approvisionnement
              de leurs rayons avec des produits de qualité aux meilleurs prix. Notre métier ? Trouver, négocier,
              livrer — vous concentrer sur votre commerce.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                ['Centrale d’achat indépendante', 'Accès direct aux marques et grossistes'],
                ['Cadencement & disponibilité', 'Réassort régulier, palettisation à la demande'],
                ['Transparence & service', 'Un interlocuteur dédié, une réponse sous 24-48h'],
              ].map(([title, desc]) => (
                <li key={title} className="flex items-start gap-3">
                  <BadgeCheck className="w-5 h-5 text-[hsl(186_55%_38%)] mt-0.5" />
                  <div>
                    <div className="font-medium">{title}</div>
                    <div className="text-muted-foreground text-xs">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex gap-3">
              <Button onClick={() => openQuote()} className="rounded-xl" data-testid="home-approvisionnement-cta">Demander un devis</Button>
              <Button asChild variant="ghost"><Link to="/a-propos">En savoir plus</Link></Button>
            </div>
          </div>
          <div className="order-1 md:order-2 grid grid-cols-2 gap-3">
            <div className="aspect-square rounded-2xl overflow-hidden border border-border">
              <img src="https://images.unsplash.com/photo-1601912552080-0fb89fd08042?auto=format&fit=crop&w=700&q=80" alt="Logistique" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-border mt-8">
              <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=700&q=80" alt="Entrepôt" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-border -mt-4">
              <img src="https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=700&q=80" alt="Produits" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-border mt-4">
              <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=700&q=80" alt="Distribution" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS TEASER */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="home-products">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px]">Les incontournables</Badge>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Chez ALOFI SA</h2>
          </div>
          <Button asChild variant="ghost" className="hover:text-[hsl(186_55%_38%)]">
            <Link to="/boutique">Voir tous les produits <ArrowRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {products.map((p) => <ProductCard key={p.slug} product={p} />)}
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px]">Pourquoi Alofisa</Badge>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Un partenaire B2B exigeant</h2>
          <p className="mt-2 text-muted-foreground">Notre engagement : une qualité constante, des prix de gros, et un service à la hauteur de votre commerce.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { Icon: ShieldCheck, title: 'Qualité garantie', text: 'Produits sélectionnés auprès de marques de confiance.' },
            { Icon: Truck, title: 'Logistique fiable', text: 'Cadencement régulier, livraisons en France.' },
            { Icon: BadgeCheck, title: 'Prix de gros', text: 'Tarification dégrressive selon volume.' },
            { Icon: Handshake, title: 'Conseil dédié', text: 'Un interlocuteur unique pour votre approvisionnement.' },
          ].map(({ Icon, title, text }) => (
            <Card key={title} className="rounded-2xl border-border bg-card p-6">
              <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[hsl(186_55%_38%)]" />
              </div>
              <h3 className="font-heading font-semibold text-base">{title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* BRANDS CAROUSEL */}
      <section className="py-14 bg-white border-y border-border overflow-hidden" data-testid="home-brands">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Nos marques partenaires</h2>
            <span className="text-sm text-muted-foreground">Et bien d’autres…</span>
          </div>
          <div className="mt-8 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-6 items-center">
            {brands.map((b) => (
              <div key={b.name} className="flex items-center justify-center h-16" data-testid={`partner-brand-${b.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                <img src={b.logo} alt={b.name} className="max-h-10 object-contain brand-logo-grayscale" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="relative overflow-hidden rounded-3xl border-border bg-[hsl(222_47%_11%)] text-white p-10 lg:p-14">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[hsl(186_55%_38%)]/15 blur-2xl" aria-hidden />
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <Badge className="bg-white/10 text-white border-white/15 tracking-[0.08em] uppercase text-[10px]">Devis gratuit</Badge>
                <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">Prêt à lancer votre approvisionnement ?</h2>
                <p className="mt-3 text-white/75">Indiquez vos besoins en quelques clics. Notre équipe revient vers vous sous 24-48h avec une proposition détaillée.</p>
              </div>
              <div className="flex md:justify-end gap-3">
                <Button onClick={() => openQuote()} size="lg" className="rounded-xl bg-white text-[hsl(222_47%_11%)] hover:bg-white/90" data-testid="home-final-cta-quote">
                  Demander un devis
                </Button>
                <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10">
                  <Link to="/contact">Nous contacter</Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
